/**
 * AI Services Extension for Scratch (PictoBlox-compatible)
 * ========================================================
 * Implements PictoBlox-compatible "aiServices" blocks.
 * Currently focuses on speech-to-text to support projects like Home Automation.
 *
 * Uses the Web Speech API (SpeechRecognition).
 */
import formatMessage from 'format-message';

class AIServicesExtension {
    constructor (runtime) {
        this.runtime = runtime;
        this._speechResult = '';
        this._isListening = false;
        
        // Initialize SpeechRecognition if available
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            this._recognition = new SpeechRecognition();
            this._recognition.continuous = false;
            this._recognition.interimResults = false;
            
            this._recognition.onresult = event => {
                if (event.results.length > 0) {
                    this._speechResult = event.results[0][0].transcript;
                }
                this._isListening = false;
            };
            
            this._recognition.onerror = event => {
                console.warn('[AIServices] Speech recognition error:', event.error);
                this._isListening = false;
            };
            
            this._recognition.onend = () => {
                this._isListening = false;
            };
        } else {
            console.warn('[AIServices] Web Speech API not supported in this browser.');
        }
    }

    getInfo () {
        return {
            id: 'aiServices',
            name: formatMessage({
                id: 'gui.extension.aiServices.name',
                default: 'AI Services'
            }),
            color1: '#4CBFE6',
            color2: '#2E8EB8',
            blocks: [
                {
                    opcode: 'speech2Text',
                    blockType: 'command',
                    text: 'recognize speech for [DURATION]s in [LANGUAGE]',
                    arguments: {
                        DURATION: {
                            type: 'number',
                            menu: 'countDown',
                            defaultValue: 2
                        },
                        LANGUAGE: {
                            type: 'string',
                            menu: 'languageMenu',
                            defaultValue: 'en-US'
                        }
                    }
                },
                {
                    opcode: 'speechResult',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'speechRecognition.blocks.getResult',
                        default: 'speech recognition result'
                    })
                }
            ],
            menus: {
                countDown: {
                    acceptReporters: true,
                    items: ['2', '3', '4', '5']
                },
                languageMenu: {
                    acceptReporters: true,
                    items: [
                        {text: 'English', value: 'en-US'},
                        {text: 'Hindi', value: 'hi-IN'},
                        {text: 'Spanish', value: 'es-ES'},
                        {text: 'French', value: 'fr-FR'}
                    ]
                }
            }
        };
    }

    // ═══════════════════════════════════════════════════════════
    //  Block implementations
    // ═══════════════════════════════════════════════════════════

    speech2Text (args) {
        return new Promise(resolve => {
            if (!this._recognition) {
                this._speechResult = 'Error: Speech Recognition not supported';
                resolve();
                return;
            }

            if (this._isListening) {
                this._recognition.stop();
            }

            this._speechResult = '';
            this._recognition.lang = args.LANGUAGE || 'en-US';
            
            try {
                this._recognition.start();
                this._isListening = true;
            } catch (e) {
                console.warn('[AIServices] Could not start recognition:', e);
                resolve();
                return;
            }

            // Stop recognition after the specified duration
            const durationMs = (Number(args.DURATION) || 2) * 1000;
            
            // Wait for duration, then stop if still listening
            setTimeout(() => {
                if (this._isListening) {
                    this._recognition.stop();
                }
                // Give it a tiny bit more time to process the final result before resolving the block
                setTimeout(resolve, 500);
            }, durationMs);
        });
    }

    speechResult () {
        return this._speechResult;
    }
}

export default AIServicesExtension;
